import { Forms, Tooltip } from "@types/FormTypes";
import { __ } from "@wordpress/i18n";
import { validations } from "./Validations";

/**
 * Form field label constants with proper Portuguese localization
 */
const LABEL_TITLE = __("Dados para a simulação", "df-simulators");
const LABEL_TITLE_INCOME = __("Rendimento mensal", "df-simulators");
const LABEL_NET_INCOME = __("Rendimento Mensal Líquido", "df-simulators");
const LABEL_EXPENSES = __("Despesas mensais", "df-simulators");
const LABEL_MORTGAGE = __("Crédito Habitação", "df-simulators");
const LABEL_CAR_LOAN = __("Crédito automóvel", "df-simulators");
const LABEL_PERSONAL_LOAN = __("Crédito pessoal", "df-simulators");
const LABEL_CREDIT_CARD = __("Dívida do cartão de crédito", "df-simulators");
const LABEL_OTHER_LOANS = __("Outros créditos", "df-simulators");
const LABEL_DOMESTIC_BURDENS = __(
  "Total de encargos domésticos",
  "df-simulators",
);
const LABEL_INSURANCES = __("Seguros", "df-simulators");
const LABEL_LIFE_INSURANCE = __("Seguro de Vida", "df-simulators");
const LABEL_MULTI_RISK = __("Seguro multiriscos", "df-simulators");

/**
 * Placeholder constants for form fields
 */
const PLACEHOLDER_INCOME = __("1000€", "df-simulators");
const PLACEHOLDER_MORTGAGE = __("0", "df-simulators");
const PLACEHOLDER_CAR_LOAN = __("0", "df-simulators");
const PLACEHOLDER_PERSONAL_LOAN = __("0", "df-simulators");
const PLACEHOLDER_CREDIT_CARD = __("0", "df-simulators");
const PLACEHOLDER_OTHER_LOANS = __("0", "df-simulators");
const PLACEHOLDER_DOMESTIC_BURDENS = __("0", "df-simulators");
const PLACEHOLDER_LIFE_INSURANCE = __("0", "df-simulators");
const PLACEHOLDER_MULTI_RISK = __("0", "df-simulators");

/**
 * Tooltip text constants with comprehensive explanations
 */
const TOOLTIPS: Record<string, Tooltip> = {
  net_monthly_income: {
    title: LABEL_NET_INCOME,
    content: __(
      "Total de rendimentos líquidos, ou seja, valores deduzidos de todos os descontos obrigatórios (IRS, Segurança Social, etc.). Inclui salário e outros rendimentos regulares como rendas ou pensões.",
      "df-simulators",
    ),
    placement: "right",
  },
  other_loans: {
    title: LABEL_OTHER_LOANS,
    content: __(
      "Valor associado a outras linhas de crédito tais como cartões de fidelização de lojas ou outros créditos ao consumo.",
      "df-simulators",
    ),
    placement: "right",
  },
};

/**
 * API configuration constants
 */
const API_BASE_URL = `${window.location.origin}/wp-json/simulators/v1/effort-rate`;
const PROCESS_TYPE = 4;
const OPERATION_TYPE = 2;


/**
 * Simulator custom URL - Check if exists some custom URL for this block
 * [==START==]
 */
let MIDDLEWARE_API_URL = API_BASE_URL;
const rootElement = document.querySelector('[data-block="tool-block"]') as HTMLElement | null;
if ( rootElement !== null ) {
  const NEW_URL = rootElement.getAttribute('data-tool-managment');
  if ( NEW_URL !== null && NEW_URL !== '' ) {
    MIDDLEWARE_API_URL = NEW_URL;
  }
}
/* [==END==] */


/**
 * Transforms form data before submission
 *
 * @param {Object} data Raw form data
 * @returns {Object} Processed data ready for API submission
 */
const transformFormData = (data: Record<string, any>) => {
  const processedData = { ...data };

  // Ensure numeric values are properly formatted
  Object.keys(processedData).forEach((key) => {
    if (typeof processedData[key] === "string") {
      const numericValue = parseFloat(processedData[key]);
      if (!isNaN(numericValue)) {
        processedData[key] = numericValue;
      }
    }
  });

  return processedData;
};

/**
 * Form configuration for Effort Rate simulator
 */
export const forms: Forms = {
  effort_rate: {
    validation: validations.effort_rate,
    api: {
      url: MIDDLEWARE_API_URL,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      processType: PROCESS_TYPE,
      operationType: OPERATION_TYPE,
      transformData: transformFormData,
    },
    fields: [
      {
        type: "title",
        label: LABEL_TITLE,
      },
      {
        type: "heading",
        label: LABEL_TITLE_INCOME,
      },
      {
        type: "number",
        label: LABEL_NET_INCOME,
        name: "net_monthly_income",
        icon: "currency",
        placeholder: PLACEHOLDER_INCOME,
        tooltip: TOOLTIPS.net_monthly_income,
        required: true,
        defaultValue: "1000",
      },
      {
        type: "heading",
        label: LABEL_EXPENSES,
      },
      {
        type: "number",
        label: LABEL_MORTGAGE,
        name: "mortgage",
        icon: "currency",
        placeholder: PLACEHOLDER_MORTGAGE,
        defaultValue: "0",
      },
      {
        type: "number",
        label: LABEL_CAR_LOAN,
        name: "car_loan",
        icon: "currency",
        placeholder: PLACEHOLDER_CAR_LOAN,
        tooltip: TOOLTIPS.car_loan,
        defaultValue: "0",
      },
      {
        type: "number",
        label: LABEL_PERSONAL_LOAN,
        name: "personal_loan",
        icon: "currency",
        placeholder: PLACEHOLDER_PERSONAL_LOAN,
        defaultValue: "0",
      },
      {
        type: "number",
        label: LABEL_CREDIT_CARD,
        name: "credit_card_debt",
        icon: "currency",
        placeholder: PLACEHOLDER_CREDIT_CARD,
        defaultValue: "0",
      },
      {
        type: "number",
        label: LABEL_OTHER_LOANS,
        name: "other_loans",
        icon: "currency",
        placeholder: PLACEHOLDER_OTHER_LOANS,
        tooltip: TOOLTIPS.other_loans,
        defaultValue: "0",
      },
      /*
      {
        type: "number",
        label: LABEL_DOMESTIC_BURDENS,
        name: "total_domestic_burdens",
        icon: "currency",
        placeholder: PLACEHOLDER_DOMESTIC_BURDENS,
        defaultValue: "0",
      },
      {
        type: "heading",
        label: LABEL_INSURANCES,
      },
      {
        type: "number",
        label: LABEL_LIFE_INSURANCE,
        name: "life_insurance",
        icon: "currency",
        placeholder: PLACEHOLDER_LIFE_INSURANCE,
        defaultValue: "0",
      },
      {
        type: "number",
        label: LABEL_MULTI_RISK,
        name: "multi_risk_insurance",
        icon: "currency",
        placeholder: PLACEHOLDER_MULTI_RISK,
        defaultValue: "0",
      },
      */
    ],
  },
};

export default forms;
